<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onBeforeUnmount, onMounted } from 'vue'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationsStore } from '@/stores/notifications.store'

const { t } = useI18n()

const ui = useUIStore()
const auth = useAuthStore()
const notifications = useNotificationsStore()

const onSearchKey = (e: KeyboardEvent) => {
  const mod = e.metaKey || e.ctrlKey
  if (mod && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    ui.toggleSearch()
  }
  if (e.key === 'Escape') ui.closeSearch()
}

/* Registered on mount and removed on unmount. It used to be added at setup
 * with no teardown, so every remount left another live listener behind — and
 * two listeners turn ⌘K into a toggle called twice, which is a no-op. */
onMounted(() => {
  window.addEventListener('keydown', onSearchKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onSearchKey)
})
</script>

<template>
  <header class="topbar">
    <button
      class="topbar__menu"
      data-action="open-sidebar"
      :aria-label="t('topbar.openMenu')"
      @click="ui.openSidebar()"
    >
      <i class="fa-solid fa-bars"></i>
    </button>

    <label class="topbar__search" @click.prevent="ui.openSearch()">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <span class="sr-only">{{ t('topbar.openSearch') }}</span>
      <input
        type="search"
        :placeholder="t('topbar.search')"
        :aria-label="`${t('topbar.openSearch')} (Cmd K)`"
        readonly
      />
      <span class="topbar__kbd" aria-hidden="true">⌘K</span>
    </label>

    <div class="topbar__spacer"></div>

    <!-- The dot is the live unread count (W11): the layout follows
         /notifications/stream, so it appears without a reload. The number
         itself is in the label and in the popup. -->
    <button
      class="topbar__action"
      :class="{ 'is-open': ui.notifOpen }"
      data-bell
      :aria-label="
        notifications.unreadCount
          ? t('topbar.notificationsUnread', { n: notifications.unreadCount })
          : t('topbar.notifications')
      "
      :aria-expanded="ui.notifOpen"
      @click.stop="ui.toggleNotif()"
    >
      <i class="fa-regular fa-bell"></i>
      <span v-if="notifications.unreadCount > 0" class="topbar__action__badge"></span>
    </button>

    <RouterLink class="topbar__action" :to="{ name: 'help' }" :aria-label="t('topbar.help')">
      <i class="fa-regular fa-circle-question"></i>
    </RouterLink>

    <button class="user" :class="{ 'is-open': ui.accountOpen }" @click.stop="ui.toggleAccount()">
      <span class="user__avatar">{{ auth.user.initials }}</span>
      <span class="user__name">{{ auth.user.name }}</span>
      <i class="fa-solid fa-chevron-down user__chev"></i>
    </button>
  </header>
</template>
